/**
 * Utility functions for ApplicationHeader component
 * Centralized logic for status display and payment information
 */

import type { ResidentApplicationData } from '@/types/application'
import { checkApplicationPaid, getApplicationStatus } from '@/types/application'

/**
 * Calculate rent added status from application data
 * @param applicationData - Application data
 * @returns Boolean indicating if rent is added
 */
export function calculateRentAdded(applicationData: ResidentApplicationData | null | undefined): boolean {
  if (!applicationData) return false

  const rentAdded = applicationData.rent_added
  if (typeof rentAdded === 'boolean') return rentAdded
  if (typeof rentAdded === 'string') return rentAdded === '1'
  if (typeof rentAdded === 'number') return rentAdded === 1
  return false
}

/**
 * Calculate status display text based on AngularJS implementation logic
 * Priority:
 * 1. progress_status if 'Credit Report Pending' or 'Application Paid'
 * 2. progress_status if exists and applicant_status is NOT 'INCOMPLETE' or 'NOT_STARTED'
 * 3. Mapped applicant_status to display name
 * @param applicationData - Application data
 * @param fallbackStatus - Fallback status text (translated)
 * @returns Status display text
 */
export function calculateStatusDisplay(
  applicationData: ResidentApplicationData | null | undefined,
  fallbackStatus: string = 'Incomplete'
): string {
  if (!applicationData) return fallbackStatus

  // Priority 1: progress_status if 'Credit Report Pending' or 'Application Paid'
  const progressStatus = applicationData.progress_status
  if (progressStatus === 'Credit Report Pending' || progressStatus === 'Application Paid') {
    return progressStatus
  }

  // Priority 2: progress_status if exists and applicant_status is NOT 'INCOMPLETE' or 'NOT_STARTED'
  const applicantStatus = applicationData.applicant_status || applicationData.application_status || ''
  if (progressStatus && applicantStatus !== 'INCOMPLETE' && applicantStatus !== 'NOT_STARTED') {
    return progressStatus
  }

  // Priority 3: Map applicant_status to display name
  const statusMap: Record<string, string> = {
    'INCOMPLETE': 'Incomplete',
    'PENDING': 'Pending',
    'APPROVED': 'Approved',
    'DENIED': 'Denied',
    'CANCELLED': 'Cancelled',
    'EXPIRED': 'Expired',
    'AWAITING': 'Awaiting',
    'VOID': 'Void',
    'NOT_STARTED': 'Not Started'
  }

  return statusMap[applicantStatus] || applicantStatus || applicationData.status || fallbackStatus
}

/**
 * Check if status indicates approval
 * @param status - Status text
 * @param applicantStatus - Applicant status code
 * @returns Boolean indicating if status is approved
 */
export function isApprovedStatus(status?: string, applicantStatus?: string): boolean {
  const statusLower = status?.toLowerCase() || ''
  const applicantStatusUpper = applicantStatus?.toUpperCase() || ''

  return (
    applicantStatusUpper === 'APPROVED' ||
    status === 'APPROVED' ||
    status === 'Approved' ||
    statusLower.includes('approved') ||
    statusLower.includes('underwriting')
  )
}

/**
 * Get applicant next-step message for display (e.g. DENIED, EXPIRED)
 * Based on workflow documentation: applicantNextStep per status
 * @param status - Applicant status (e.g. DENIED, EXPIRED)
 * @returns Next step message or empty string
 */
export function getApplicantNextStep(status: string | undefined): string {
  if (!status) return ''
  const upper = status.toUpperCase()
  const nextSteps: Record<string, string> = {
    INCOMPLETE: 'Finish the application and pay application fee',
    PENDING: 'Liberty Rent is reviewing your Application and will send an update shortly',
    APPROVED: 'Pay the Access Fee and get ready to move in',
    DENIED: 'We are sorry but you did not meet our minimum requirements',
    EXPIRED: 'Expired',
    AWAITING: 'Correct your application and resubmit for review',
    NOT_STARTED: 'Please complete the application',
    CANCELLED: 'Cancelled',
  }
  return nextSteps[upper] || ''
}

/**
 * Get all ApplicationHeader props from application data
 * Centralized function to ensure consistency across all pages
 * @param applicationData - Application data
 * @param fallbackStatus - Fallback status text (translated)
 * @returns Object with all ApplicationHeader props
 */
export function getApplicationHeaderProps(
  applicationData: ResidentApplicationData | null | undefined,
  fallbackStatus: string = 'Incomplete'
) {
  const rentAdded = calculateRentAdded(applicationData)
  const status = calculateStatusDisplay(applicationData, fallbackStatus)
  const applicantStatus = applicationData?.applicant_status || applicationData?.application_status || ''
  const nextStep = getApplicantNextStep(applicantStatus)

  // Submit for Processing: show when application fee paid, allow_edit, status AWAITING or VOID.
  // Angular does NOT hide the button when locked — it stays visible but the form-readonly class blocks clicks.
  const statusUpper = getApplicationStatus(applicationData)
  // Angular uses: this.isPaymentAdded = this.applicationData.payment_log_id ? true : false
  const isPaymentAdded = !!applicationData?.payment_log_id || checkApplicationPaid(applicationData)
  const isAllowEdit = !!applicationData?.allow_edit
  const isLocked = applicationData?.applicant_lock === '1'
  const isAwaitingOrVoid = statusUpper === 'AWAITING' || statusUpper === 'VOID'
  const showSubmitForProcessing = !!(
    isPaymentAdded &&
    isAllowEdit &&
    isAwaitingOrVoid
  )

  return {
    applicantId: String(applicationData?.applicant_id || '-'),
    applicationId: String(applicationData?.fk_application_id || applicationData?.application_id || '-'),
    status,
    rentAdded,
    balanceDue: applicationData?.balance_due ? Number(applicationData.balance_due) : undefined,
    paymentDueDate: applicationData?.payment_due_date,
    applicantStatus,
    nextStep,
    showSubmitForProcessing,
    isApplicationLocked: isLocked,
    coApplicants: undefined // TODO: Implement co-applicant extraction if needed
  }
}

